import { Sparkles, Cpu, Zap, Rocket } from "lucide-react";

/**
 * WJAnnotations — right-side "system detail" labels connected to the WJ
 * Core via thin trajectory arcs. Signal-flow pulses travel along each arc
 * continuously via native CSS `offset-path` animation — no per-frame
 * React reconciliation, all on the compositor thread.
 *
 * Hidden below `xl:` breakpoint to keep tablet/mobile clean and light.
 */

const ITEMS = [
  {
    icon: Sparkles,
    label: "Design System",
    desc: "Unified. Scalable. Consistent.",
    top: "18%",
    color: "#EFE4CE",
    path: "M 1190 500 Q 1400 210 1660 200",
  },
  {
    icon: Cpu,
    label: "Intelligence Layer",
    desc: "Context-aware. Adaptive.",
    top: "38%",
    color: "#3fdca6",
    path: "M 1190 500 Q 1420 380 1660 400",
  },
  {
    icon: Zap,
    label: "Performance Engine",
    desc: "Optimised for speed and scale.",
    top: "58%",
    color: "#F4E3B0",
    path: "M 1190 500 Q 1420 620 1660 600",
  },
  {
    icon: Rocket,
    label: "Launch Trajectory",
    desc: "Momentum is the new advantage.",
    top: "78%",
    color: "#2B8AF5",
    path: "M 1190 500 Q 1400 800 1660 800",
  },
];

export default function WJAnnotations() {
  return (
    <>
      {/* Trajectory arcs from the WJ Core outward to each annotation.
          Arcs are static; only the signal pulses move (native CSS). */}
      <svg
        viewBox="0 0 1920 1000"
        preserveAspectRatio="none"
        className="hidden xl:block absolute inset-0 w-full h-full pointer-events-none"
        aria-hidden="true"
      >
        <defs>
          <linearGradient id="wj-traj-em" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#3fdca6" stopOpacity="0" />
            <stop offset="55%" stopColor="#B79961" stopOpacity="0.5" />
            <stop offset="100%" stopColor="#B79961" stopOpacity="0.85" />
          </linearGradient>
        </defs>

        {ITEMS.map((item) => (
          <path
            key={item.label}
            d={item.path}
            fill="none"
            stroke="url(#wj-traj-em)"
            strokeWidth="0.75"
            strokeDasharray="2 5"
            opacity="0.55"
          />
        ))}

        {/* Continuous signal-flow pulses — pure CSS via offset-path.
            Each carrier gets its own delay so signals arrive at annotations
            at different times, giving the right side a live orchestrated
            network feeling. */}
        {ITEMS.map((item, i) => (
          <g key={`pulse-${i}`}>
            {/* Primary bright pulse */}
            <circle
              className="wj-signal"
              r="3"
              fill={item.color}
              style={{
                offsetPath: `path("${item.path}")`,
                animationDelay: `${i * 1.15}s`,
              }}
            />
            {/* Trailing softer pulse for signal density */}
            <circle
              className="wj-signal"
              r="1.7"
              fill={item.color}
              opacity="0.55"
              style={{
                offsetPath: `path("${item.path}")`,
                animationDelay: `${i * 1.15 + 2.3}s`,
              }}
            />
          </g>
        ))}
      </svg>

      {/* Annotation labels */}
      <div className="hidden xl:block absolute right-8 2xl:right-14 inset-y-0 pointer-events-none">
        {ITEMS.map(({ icon: Icon, label, desc, top }, i) => (
          <div
            key={label}
            className="absolute right-0 flex items-start gap-3 max-w-[230px] wj-anno-fade-in"
            style={{ top, animationDelay: `${1.4 + i * 0.18}s` }}
          >
            {/* Icon in a gold-bordered pill */}
            <div
              className="mt-0.5 w-9 h-9 rounded-full border border-[#B79961]/45 flex items-center justify-center shrink-0"
              style={{
                boxShadow: "0 0 18px rgba(183,153,97,0.08) inset",
              }}
            >
              <Icon className="w-4 h-4 text-[#EFE4CE]/90" strokeWidth={1.4} />
            </div>

            <div className="flex flex-col leading-tight pt-1">
              <div className="flex items-center gap-2">
                <span
                  className="block h-px w-4"
                  style={{
                    background:
                      "linear-gradient(90deg, rgba(183,153,97,0.5), rgba(183,153,97,0))",
                  }}
                />
                <span className="text-[10px] tracking-[0.28em] uppercase text-[#EFE4CE]/90 font-body">
                  {label}
                </span>
              </div>
              <span className="mt-1.5 text-[10.5px] leading-relaxed text-white/45 font-body">
                {desc}
              </span>
            </div>
          </div>
        ))}
      </div>
    </>
  );
}
